import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePublicClinicRef } from '@/hooks/useClinics';
import { tenantHref } from '@/routing/tenant';

/**
 * Forward a QR code printed before clinics had slugs to its new home.
 *
 * The old links carry a raw clinic id — `/p/register/<uuid>` — which the API
 * still resolves, but the SPA no longer has a route for: those pages now live
 * inside the clinic's namespace at `/<slug>/p/register`. Rather than keeping a
 * second copy of each page alive at the old path, we look the clinic up and
 * replace the URL.
 *
 * `replace` rather than `assign` so the dead link does not sit in the back stack
 * — a patient tapping Back from the form should reach whatever they were doing
 * before scanning, not bounce through the redirect again.
 *
 * These posters are wall-mounted and will keep being scanned for a long time, so
 * this is not a transitional shim on a timer. It stays.
 */
export function LegacyPublicRedirect({ page }: { page: 'counter' | 'register' }) {
  const { clinicId } = useParams<{ clinicId: string }>();
  const { data: clinic, isError } = usePublicClinicRef(clinicId);

  const slug = clinic?.slug;
  useEffect(() => {
    if (!slug) return;
    // The querystring is carried over: the registration poster encodes the
    // printed language there, and dropping it would show a Sinhala poster's
    // visitor an English form.
    window.location.replace(tenantHref(slug, `/p/${page}${window.location.search}`));
  }, [slug, page]);

  if (isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6 text-center text-[13px] text-muted">
        This link is no longer valid. Please ask the clinic for a current one.
      </div>
    );
  }
  return null;
}
